import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Download } from '@mui/icons-material';
import api from '../services/api';
import { LotProfitabilityReport } from '../types';
import { formatDay, formatMoney } from '../components/commercial/contact';

const Kpi: React.FC<{ label: string; value: string; hint?: string; color?: string }> = ({ label, value, hint, color }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h5" fontWeight={800} sx={{ color }}>{value}</Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </CardContent>
  </Card>
);

const LotProfitabilityPage: React.FC = () => {
  const [data, setData] = useState<LotProfitabilityReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [onlyProduced, setOnlyProduced] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.getLotProfitability({ from: from || undefined, to: to || undefined, onlyProduced }));
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo cargar la rentabilidad por lote');
    } finally {
      setLoading(false);
    }
  }, [from, to, onlyProduced]);

  useEffect(() => { load(); }, [load]);

  const lots = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.lots || []).filter(l => !q
      || l.productName.toLowerCase().includes(q)
      || (l.batchCode || '').toLowerCase().includes(q)
      || (l.productionOrderNumber || '').toLowerCase().includes(q));
  }, [data, search]);

  const exportCsv = () => {
    const header = ['Producto', 'Lote', 'OP', 'Fecha', 'Vencimiento', 'Costo unitario', 'Producido', 'Vendido', 'En stock', 'Regalado', 'Perdido', 'Facturado', 'Costo vendido', 'Ganancia', 'Margen %', 'Precio promedio', 'Ganancia proyectada'];
    const rows = lots.map(l => [
      l.productName, l.batchCode || '', l.productionOrderNumber || '', formatDay(l.createdAt), formatDay(l.expirationDate),
      l.unitCost, l.producedQuantity, l.soldQuantity, l.remainingQuantity, l.giftedQuantity || 0, l.lostQuantity || 0, l.revenue, l.costOfSold, l.grossProfit, l.marginPct, l.avgSalePrice, l.projectedProfit,
    ]);
    const csv = [header, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';'))
      .join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rentabilidad-por-lote.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Rentabilidad por lote</Typography>
          <Typography variant="body2" color="text.secondary">
            Cuánto costó producir cada unidad del lote, a cuánto se vendió y cuánta ganancia dejó (montos sin IVA). Los regalos y pérdidas restan su costo.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Download />} onClick={exportCsv} disabled={!lots.length}>Exportar CSV</Button>
      </Stack>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField size="small" label="Buscar producto o lote" value={search} onChange={e => setSearch(e.target.value)} sx={{ minWidth: 220 }} />
            <TextField size="small" type="date" label="Lotes desde" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="date" label="Hasta" value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
            <FormControlLabel control={<Switch checked={onlyProduced} onChange={e => setOnlyProduced(e.target.checked)} />} label="Solo lotes producidos" />
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {data && data.unattributedSoldQuantity > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Hay {data.unattributedSoldQuantity} unidades vendidas de estos productos que no quedaron asignadas a un lote
          (ventas anteriores a activar el descuento por lote). No se incluyen en la ganancia por lote.
        </Alert>
      )}

      {data && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} md={3}><Kpi label="Facturado" value={formatMoney(data.totalRevenue)} /></Grid>
          <Grid item xs={6} md={3}><Kpi label="Costo de lo vendido" value={formatMoney(data.totalCostOfSold)} /></Grid>
          <Grid item xs={6} md={3}><Kpi label="Ganancia bruta" value={formatMoney(data.totalGrossProfit)} color={data.totalGrossProfit >= 0 ? 'success.main' : 'error.main'} /></Grid>
          <Grid item xs={6} md={3}><Kpi label="Margen" value={`${data.marginPct}%`} hint="ganancia / facturado" /></Grid>
        </Grid>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <Card variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Lote</TableCell>
                  <TableCell align="right">Costo / u</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Producido</TableCell>
                  <TableCell align="right">Vendido</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>En stock</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Regalado / perdido</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Precio prom.</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Facturado</TableCell>
                  <TableCell align="right">Ganancia</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Si vende el resto</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lots.map(l => (
                  <TableRow key={l.batchId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{l.productName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {l.batchCode || 'Sin código'}{l.productionOrderNumber ? ` · ${l.productionOrderNumber}` : ''} · {formatDay(l.createdAt)}
                        {l.expirationDate ? ` · vence ${formatDay(l.expirationDate)}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{formatMoney(l.unitCost)}</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>{l.producedQuantity}</TableCell>
                    <TableCell align="right">{l.soldQuantity}</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{l.remainingQuantity}</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {(l.giftedQuantity || 0) + (l.lostQuantity || 0) > 0 ? `${l.giftedQuantity || 0} / ${l.lostQuantity || 0}` : '—'}
                      {l.giftLossCost ? <Typography variant="caption" display="block" color="text.secondary">−{formatMoney(l.giftLossCost)}</Typography> : null}
                    </TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>{l.soldQuantity > 0 ? formatMoney(l.avgSalePrice) : '—'}</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>{formatMoney(l.revenue)}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={700} color={l.grossProfit >= 0 ? 'success.main' : 'error.main'}>
                        {formatMoney(l.grossProfit)}
                      </Typography>
                      {l.revenue > 0 && <Chip size="small" label={`${l.marginPct}%`} sx={{ mt: 0.5 }} />}
                    </TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{formatMoney(l.projectedProfit)}</TableCell>
                  </TableRow>
                ))}
                {lots.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10}>
                      <Typography variant="body2" color="text.secondary">
                        Sin lotes. Los lotes se crean al completar una orden de producción.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </Box>
  );
};

export default LotProfitabilityPage;
